/** Native message funding is not the final network fee: unused gas is returned.
 * Keep the wallet's own fee headroom separate; it is never added to the payload.
 * Integer-only and shared by the server guard and the client presentation.
 */
export const SWAP_WALLET_POLICY = { feeHeadroomNano: "10000000", balanceRefreshMs: 15000 } as const;

export type SwapWalletRequirements = {
  gramNano: string;
  tokenNano: string;
  messageGramNano: string;
  walletFeeHeadroomNano: string;
};
export type SwapWalletBalances = { gramNano: string; tokenNano: string };
export type SwapWalletFunding = {
  sufficient: boolean;
  missingGramNano: string;
  missingTokenNano: string;
};
function units(value: string): bigint {
  if (!/^(0|[1-9]\d{0,35})$/.test(value)) throw new Error("market_swap_funding_invalid");
  return BigInt(value);
}
export function swapWalletRequirements(direction: "buy" | "sell", offerUnits: string, messages: readonly { amount: string }[]): SwapWalletRequirements {
  const offer = units(offerUnits);
  if (offer <= 0n || !["buy", "sell"].includes(direction) || messages.length < 1 || messages.length > 4) throw new Error("market_swap_funding_invalid");
  const messageGram = messages.reduce((sum, message) => sum + units(message.amount), 0n);
  if (messageGram <= 0n || (direction === "buy" && messageGram < offer)) throw new Error("market_swap_funding_invalid");
  return { gramNano: (messageGram + BigInt(SWAP_WALLET_POLICY.feeHeadroomNano)).toString(),
    tokenNano: direction === "sell" ? offer.toString() : "0", messageGramNano: messageGram.toString(),
    walletFeeHeadroomNano: SWAP_WALLET_POLICY.feeHeadroomNano };
}
export function swapWalletFunding(required: SwapWalletRequirements, balances: SwapWalletBalances): SwapWalletFunding {
  const missingGram = units(required.gramNano) - units(balances.gramNano);
  const missingToken = units(required.tokenNano) - units(balances.tokenNano);
  return { sufficient: missingGram <= 0n && missingToken <= 0n,
    missingGramNano: (missingGram > 0n ? missingGram : 0n).toString(), missingTokenNano: (missingToken > 0n ? missingToken : 0n).toString() };
}
/** Maximum never spends the separately displayed network/wallet headroom. */
export function swapMaximumOffer(direction: "buy" | "sell", offerUnits: string, required: SwapWalletRequirements, balances: SwapWalletBalances): string {
  const offer = units(offerUnits), gram = units(balances.gramNano);
  const maximum = direction === "sell" ? units(balances.tokenNano) : gram - (units(required.gramNano) - offer);
  return (maximum > 0n ? maximum : 0n).toString();
}
